// frontend/src/utils/exportLedgerCSV.js
//
// Export the cashbook journal as a CSV you can actually open and pivot.
//
// Three details decide whether this lands as a spreadsheet or as garbage, and
// all three are locale problems rather than code problems:
//
//   * SEPARATOR. French-locale Excel — which is what every machine here runs —
//     uses ";" as the list separator, because "," is the decimal mark. A
//     comma-separated file opens with every row crammed into column A.
//
//   * BOM. Without a leading U+FEFF, Excel reads a UTF-8 file as ANSI and
//     "Réglé" arrives as "RÃ©glÃ©". Every accented category name in the
//     cashbook would be mangled.
//
//   * NUMBERS. Amounts are written as bare integers with no grouping. FCFA has
//     no minor unit, so there is nothing to lose, and "230000" is read as a
//     number in every locale while "230 000" is read as text — which silently
//     turns a SUM into zero.
//
// There is deliberately no totals row. It would sit inside the data range and
// break sorting, filtering and any pivot built on top.

const SEP = ';';

/**
 * CSV-quote one field. Quotes are doubled, and anything containing the
 * separator, a quote or a newline is wrapped.
 */
export function csvCell(value) {
  if (value === null || value === undefined) return '';

  // Numbers are written verbatim and are never formula-guarded. A negative
  // amount starts with "-", and apostrophe-escaping it would make every
  // outgoing line TEXT — which is the "SUM returns 0" failure this file exists
  // to prevent. Only free text can smuggle a formula in.
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';

  const s = String(value);
  if (s === '') return '';
  // A leading =, +, - or @ makes Excel treat the cell as a formula. A category
  // named "=SUM(A1)" is unlikely, but a reference like "-2026/08" is not, and
  // a spreadsheet that executes its own data is a hazard worth closing.
  const guarded = /^[=+\-@]/.test(s) ? `'${s}` : s;
  if (guarded.includes(SEP) || guarded.includes('"') || /[\r\n]/.test(guarded)) {
    return `"${guarded.replace(/"/g, '""')}"`;
  }
  return guarded;
}

/** dd/mm/yyyy — what Excel FR reads as a date without being asked twice. */
export function csvDate(d) {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(t.getDate())}/${p(t.getMonth() + 1)}/${t.getFullYear()}`;
}

export const LEDGER_COLUMNS = [
  'Date',
  'Type',
  'Catégorie',
  'Véhicule',
  'Référence',
  'Motif',
  'Entrée',
  'Sortie',
  'Montant signé',
  'État',
  'Échéance',
  'Réglé le',
  'Enregistré le',
];

/**
 * One journal entry as a row of cells.
 *
 * `Entrée` / `Sortie` are the shape a livre de caisse is read in; `Montant
 * signé` is the same figure as one number, which is what a pivot table wants.
 * Both are given rather than making the reader choose.
 */
export function ledgerRow(entry, { plateOf = () => '' } = {}) {
  const amount = Math.round(Number(entry?.amount) || 0);
  const isIn = entry?.type === 'in';
  const pending = entry?.settlement === 'pending';

  return [
    csvDate(entry?.date),
    isIn ? 'Entrée' : 'Sortie',
    entry?.category || '',
    plateOf(entry?.vehicleId) || '',
    entry?.reference || '',
    entry?.reason || '',
    isIn ? amount : '',
    isIn ? '' : amount,
    isIn ? amount : -amount,
    // The distinction the cash balance turns on: a pending entry is a
    // receivable or a payable, and the money has not moved.
    pending ? (isIn ? 'À recevoir' : 'À payer') : 'Réglé',
    csvDate(entry?.dueDate),
    csvDate(entry?.settledAt),
    csvDate(entry?.createdAt),
  ];
}

/**
 * @param {Array} entries    journal entries, already filtered as displayed
 * @param {Object} [opts]
 * @param {Function} [opts.plateOf]  vehicleId -> plate number
 * @returns {string} CSV text, BOM included
 */
export function buildLedgerCSV(entries = [], opts = {}) {
  const lines = [LEDGER_COLUMNS.map(csvCell).join(SEP)];
  for (const e of entries) {
    lines.push(ledgerRow(e, opts).map(csvCell).join(SEP));
  }
  // CRLF: Excel is the target reader and it is the only one that cares.
  return `﻿${lines.join('\r\n')}\r\n`;
}

/** "livre-de-caisse-2026-08-01_2026-08-31.csv" */
export function ledgerFileName({ from, to } = {}) {
  const today = new Date().toISOString().slice(0, 10);
  if (from || to) return `livre-de-caisse-${from || 'debut'}_${to || today}.csv`;
  return `livre-de-caisse-${today}.csv`;
}

/**
 * Build the file and hand it to the browser.
 *
 * text/csv rather than application/octet-stream so a phone offers to open it
 * in a spreadsheet app instead of only saving it.
 */
export function downloadLedgerCSV(entries, { from, to, plateOf } = {}) {
  const csv = buildLedgerCSV(entries, { plateOf });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ledgerFileName({ from, to });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return csv;
}
